import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for theme management
 * Handles theme state, localStorage persistence, and system preference detection
 */
export const useTheme = () => {
  const [theme, setTheme] = useState('light');

  const getPreferredTheme = useCallback(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  const applyTheme = useCallback((newTheme) => {
    document.documentElement.setAttribute('data-theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
    // Keep the mobile status-bar tint (theme-color meta) matching the app theme:
    // true black in dark, the header's near-white in light.
    const meta = document.getElementById('theme-color-meta')
      || document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', newTheme === 'dark' ? '#000000' : '#FEFCFA');
    localStorage.setItem('theme', newTheme);
    setTheme(newTheme);
  }, []);

  // Initialize theme on mount
  useEffect(() => {
    const preferredTheme = getPreferredTheme();
    applyTheme(preferredTheme);
  }, [getPreferredTheme, applyTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (e) => {
      if (!localStorage.getItem('theme')) {
        const newTheme = e.matches ? 'dark' : 'light';
        applyTheme(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleThemeChange);
    return () => mediaQuery.removeEventListener('change', handleThemeChange);
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    applyTheme(newTheme);
  }, [theme, applyTheme]);

  return { theme, toggleTheme };
};

